import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { resolveGuardAction, SSOAttemptKey, LoginRedirectKey } from './guard'

export { SSOAttemptKey, LoginRedirectKey }

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('@/pages/Home.vue') },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFound.vue'),
      },
    ],
  },
  {
    path: '/account',
    component: () => import('@/layouts/AccountLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'account', redirect: { name: 'account-profile' } },
      {
        path: 'profile',
        name: 'account-profile',
        component: () => import('@/pages/account/Profile.vue'),
      },
      {
        path: 'orgs',
        name: 'account-orgs',
        component: () => import('@/pages/account/orgs/List.vue'),
      },
      {
        path: 'orgs/new',
        name: 'account-orgs-new',
        component: () => import('@/pages/account/orgs/New.vue'),
      },
      {
        path: 'orgs/:name/members/:userId',
        name: 'org-member-detail',
        component: () => import('@/pages/account/orgs/MemberDetail.vue'),
        props: true,
      },
      {
        path: 'orgs/:name/teams/:teamSlug',
        name: 'org-team-detail',
        component: () => import('@/pages/account/orgs/TeamDetail.vue'),
        props: true,
      },
      {
        path: 'orgs/:name',
        component: () => import('@/pages/account/orgs/Detail.vue'),
        props: true,
        children: [
          {
            path: '',
            redirect: (to) => ({ name: 'org-members', params: to.params }),
          },
          {
            path: 'members',
            name: 'org-members',
            component: () => import('@/pages/account/orgs/tabs/Members.vue'),
            props: true,
          },
          {
            path: 'invites',
            name: 'org-invites',
            component: () => import('@/pages/account/orgs/tabs/Invites.vue'),
            props: true,
          },
          {
            path: 'teams',
            name: 'org-teams',
            component: () => import('@/pages/account/orgs/tabs/Teams.vue'),
            props: true,
          },
          {
            path: 'audit',
            name: 'org-audit',
            component: () => import('@/pages/account/orgs/tabs/Audit.vue'),
            props: true,
          },
          {
            path: 'modules',
            name: 'org-modules',
            component: () => import('@/pages/account/orgs/tabs/Modules.vue'),
            props: true,
          },
          {
            path: 'billing',
            name: 'org-billing',
            component: () => import('@/pages/account/orgs/tabs/Billing.vue'),
            props: true,
          },
          {
            path: 'settings',
            name: 'org-settings',
            component: () => import('@/pages/account/orgs/tabs/Settings.vue'),
            props: true,
          },
        ],
      },
      { path: 'join', name: 'account-join', component: () => import('@/pages/account/Join.vue') },
      {
        path: 'modules',
        name: 'account-modules',
        component: () => import('@/pages/account/Modules.vue'),
      },
      {
        path: 'modules/new',
        name: 'account-module-new',
        component: () => import('@/pages/account/ModuleNew.vue'),
      },
      {
        path: 'modules/deleted',
        name: 'account-modules-deleted',
        component: () => import('@/pages/account/DeletedModules.vue'),
      },
      {
        path: 'modules/:org/:name',
        name: 'account-module-detail',
        component: () => import('@/pages/account/ModuleDetail.vue'),
        props: true,
      },
      {
        path: 'tokens',
        name: 'account-tokens',
        component: () => import('@/pages/account/Tokens.vue'),
      },
      {
        path: 'mcp',
        name: 'account-mcp',
        component: () => import('@/pages/account/MCP.vue'),
      },
      {
        path: 'security',
        name: 'account-security',
        component: () => import('@/pages/account/Security.vue'),
      },
      {
        path: 'security/password',
        name: 'account-security-password',
        component: () => import('@/pages/account/security/Password.vue'),
      },
      {
        path: 'security/two-factor',
        name: 'account-security-two-factor',
        component: () => import('@/pages/account/security/TwoFactor.vue'),
      },
      {
        path: 'security/linked-accounts',
        name: 'account-security-linked-accounts',
        component: () => import('@/pages/account/security/LinkedAccounts.vue'),
      },
      {
        path: 'sessions',
        name: 'account-sessions',
        component: () => import('@/pages/account/Sessions.vue'),
      },
      // /account/delete folded into the Profile page's Danger zone.
      { path: 'delete', redirect: { name: 'account-profile' } },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // Always hydrate auth state on first navigation so the header renders
  // the right side (login button vs user menu) without a second round-trip.
  await auth.ensureLoaded()
  // A transient /auth/me failure is not proof of logout. Let the app mount
  // while the bounded store retry recovers instead of starting a login loop.
  if (auth.isUnavailable) return true

  const hasWindow = typeof window !== 'undefined'
  const action = resolveGuardAction({
    isAuthenticated: auth.isAuthenticated,
    requiresAuth: to.meta.requiresAuth === true,
    fullPath: to.fullPath,
    getFlag: (key) => hasWindow && sessionStorage.getItem(key) === '1',
    setFlag: (key) => {
      if (hasWindow) sessionStorage.setItem(key, '1')
    },
    clearFlag: (key) => {
      if (hasWindow) sessionStorage.removeItem(key)
    },
  })

  if (action.kind === 'navigate') {
    if (hasWindow) window.location.assign(action.url)
    return false
  }
  return true
})

// Per-route robots meta. Authenticated surfaces stay noindex — there's
// nothing for a crawler to see and indexing session-shaped URLs is pure
// downside. Public pages (home, 404) drop the tag so Lighthouse SEO can
// grade them properly instead of flagging "blocked from indexing".
router.afterEach((to) => {
  const shouldBlock = to.meta.requiresAuth === true
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (shouldBlock) {
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'robots'
      document.head.appendChild(meta)
    }
    meta.content = 'noindex'
  } else if (meta) {
    meta.remove()
  }
})
